// INTENT: Stimulus controller for print report pages to automatically preload all off-screen charts before opening the window.print() dialog.
// EXPECTED OUTCOME: Displays a progress modal while enqueuing all chart components on the page, updating progress bar in real-time as loading targets hide, then triggering window.print() once 100% ready.
// CAVEAT/TRAP: Checks actual DOM state (loadingTarget.classList.contains("hidden")) to accurately detect when Chart.js canvas rendering completes.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "statusText", "progressBar"]

  connect() {
    // INTENT: Track printing state to prevent duplicate print dialog triggers.
    this.printed = false

    // INTENT: Delay 50ms so all Stimulus controllers on the page complete initial DOM binding.
    setTimeout(() => {
      this.initPreloader()
    }, 50)
  }

  initPreloader() {
    this.chartEls = Array.from(document.querySelectorAll('[data-controller~="async-chart"]'))
    this.totalCount = this.chartEls.length

    if (this.totalCount === 0) {
      this.finishAndPrint()
      return
    }

    this.showModal()
    this.updateProgressAndCheckCompletion()

    // INTENT: Force load all chart elements immediately.
    // EXPECTED OUTCOME: Offscreen charts begin fetching in background via ChartRequestQueue.
    this.chartEls.forEach((chartEl) => {
      const controller = this.application.getControllerForElementAndIdentifier(chartEl, "async-chart")
      if (controller && typeof controller.forceLoad === 'function') {
        controller.forceLoad()
      } else {
        chartEl.dispatchEvent(new CustomEvent("async-chart:force-load"))
      }
    })

    // INTENT: Periodically check chart DOM rendering state every 150ms.
    this.pollInterval = setInterval(() => {
      this.updateProgressAndCheckCompletion()
    }, 150)

    // INTENT: Safety timeout updates status message if network/queue is slow, requiring explicit user action rather than printing incomplete placeholders.
    // EXPECTED OUTCOME: Avoids prematurely opening print dialog while ChartRequestQueue is retrying or processing slow requests.
    this.safetyTimeout = setTimeout(() => {
      if (!this.printed && this.hasStatusTextTarget) {
        this.timedOut = true
        this.statusTextTarget.textContent = "Chart loading is taking longer than expected. Click 'Skip wait & print now' or wait for completion."
      }
    }, 15000)
  }

  updateProgressAndCheckCompletion() {
    // INTENT: Count how many charts have successfully loaded and rendered canvas.
    // EXPECTED OUTCOME: Errored charts do not increment success count, preventing auto-print on chart fetch errors.
    // CAVEAT/TRAP: Checking controller.chartLoaded ensures failed requests keep print preloader pending for manual action.
    let completed = 0

    this.chartEls.forEach((chartEl) => {
      const controller = this.application.getControllerForElementAndIdentifier(chartEl, "async-chart")
      if (controller && controller.chartLoaded) {
        completed++
      } else if (controller && !controller.chartFailed && typeof controller.forceLoad === 'function') {
        // INTENT: Only trigger forceLoad for charts that haven't been attempted or failed.
        // EXPECTED OUTCOME: Errored charts do not cause repeated 150ms retry fetches during preloader polling.
        controller.forceLoad()
      }
    })


    const percent = Math.round((completed / this.totalCount) * 100)

    if (this.hasStatusTextTarget && !this.timedOut) {
      this.statusTextTarget.textContent = `Loading report charts... ${completed} of ${this.totalCount} ready (${percent}%)`
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }

    if (completed >= this.totalCount) {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      setTimeout(() => {
        this.finishAndPrint()
      }, 350)
    }
  }

  showModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      this.modalTarget.classList.add("flex")
    }
  }

  hideModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.classList.remove("flex")
    }
  }

  finishAndPrint() {
    if (this.printed) return
    this.printed = true

    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }

    if (this.safetyTimeout) {
      clearTimeout(this.safetyTimeout)
      this.safetyTimeout = null
    }

    this.hideModal()

    // INTENT: Short delay allows DOM modal removal repaint before browser locks screen with window.print() dialog.
    setTimeout(() => {
      window.print()
    }, 300)
  }

  manualPrint() {
    this.finishAndPrint()
  }

  disconnect() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    if (this.safetyTimeout) {
      clearTimeout(this.safetyTimeout)
      this.safetyTimeout = null
    }
  }
}
